<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, useId } from "vue";

/**
 * Значок «?» с пояснением к цифре.
 *
 * Зачем: в полосе фактов стоят голые числа — 13, 5, 1 000, 3. Каждое из них
 * что-то означает, и почти каждое можно понять неверно. «Цена от 1 000» — это
 * оферта? «Рейтинг 5» — по чьим отзывам? Число без границ применимости на
 * юридическом сайте хуже отсутствующего числа. Пояснение должно быть рядом,
 * но не занимать место: цифру читают, пояснение спрашивают.
 *
 * ⚠ ПОЗИЦИЯ FIXED, А НЕ ABSOLUTE. У полосы фактов `overflow-hidden` (иначе
 * скруглённые углы режут заливку ячеек), и подсказка внутри ячейки была бы
 * обрезана её границей. Fixed вырывается из любого обрезающего предка, но за
 * это приходится считать координаты руками — что и делает place().
 *
 * Текст ВСЕГДА лежит в разметке и прячется только видимостью: через v-if он
 * исчез бы из HTML, а это единственное место, где сказано, что означает цифра.
 */
const props = defineProps({
  title: { type: String, default: "" },
  text: { type: String, required: true },
  label: { type: String, default: "Что это значит" },
});

const uid = useId();
const open = ref(false);
const btn = ref(null);
const tip = ref(null);
const pos = ref({ top: 0, left: 0, below: false });

const M = 8; // поле от края экрана

async function place() {
  if (typeof window === "undefined") return;
  await nextTick();
  const b = btn.value?.getBoundingClientRect();
  const t = tip.value;
  if (!b || !t) return;

  const w = t.offsetWidth;
  const h = t.offsetHeight;
  const vw = document.documentElement.clientWidth;

  // Хотим по центру значка, но не за краем экрана.
  const left = Math.max(M, Math.min(b.left + b.width / 2 - w / 2, vw - w - M));
  // Сверху, если там помещается; иначе снизу — у верхних блоков страницы
  // места над значком может не быть.
  const below = b.top - h - 10 < M;
  pos.value = { left, top: below ? b.bottom + 10 : b.top - h - 10, below };
}

/**
 * ⚠ Закрепление. На компьютере подсказка открывается наведением, и без этого
 * флага клик по значку её ЗАКРЫВАЛ: курсор уже над значком, mouseenter успел
 * открыть, а click переключал обратно. Человек нажимает на «?» — и ничего не
 * происходит. Теперь клик закрепляет подсказку: она переживает уход курсора,
 * пока не нажмут мимо или Escape.
 */
const pinned = ref(false);

const reflow = () => { if (open.value) place(); };
const onDocClick = (e) => {
  if (!btn.value?.contains(e.target)) { pinned.value = false; open.value = false; }
};
const onEsc = (e) => { if (e.key === "Escape") { pinned.value = false; open.value = false; } };

onMounted(() => {
  document.addEventListener("click", onDocClick);
  window.addEventListener("resize", reflow);
  // passive: подсказка на fixed не едет вместе со страницей, её надо
  // переставлять на прокрутке — но тормозить прокрутку мы не имеем права.
  window.addEventListener("scroll", reflow, { passive: true });
  document.addEventListener("keydown", onEsc);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  window.removeEventListener("resize", reflow);
  window.removeEventListener("scroll", reflow);
  document.removeEventListener("keydown", onEsc);
});

// Наведение только там, где мышь есть: на тач-экране hover залипает после
// касания и подсказка не закрывается.
const hoverable = () => typeof window !== "undefined"
  && window.matchMedia?.("(hover: hover)").matches;

const show = () => { if (hoverable()) { open.value = true; place(); } };
const hide = () => { if (hoverable() && !pinned.value) open.value = false; };
const toggle = () => {
  pinned.value = !pinned.value;
  open.value = pinned.value;
  if (open.value) place();
};
</script>

<template>
  <span class="inline-flex align-middle">
    <button
      ref="btn"
      type="button"
      :id="uid + '-btn'"
      :aria-describedby="uid"
      :aria-expanded="open"
      :aria-label="label"
      class="inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] font-bold leading-none
             transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      :class="open
        ? 'border-indigo-600 bg-indigo-600 text-white'
        : 'border-gray-300 text-gray-400 hover:border-indigo-400 hover:text-indigo-600'"
      @mouseenter="show" @mouseleave="hide" @focus="show" @blur="hide"
      @click.stop.prevent="toggle"
    >?</button>

    <span
      ref="tip"
      :id="uid"
      role="tooltip"
      class="fixed z-50 w-64 max-w-[calc(100vw-1rem)] rounded-xl bg-gray-900 px-3.5 py-3 text-left
             shadow-xl transition-opacity duration-150"
      :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
      :class="open ? 'opacity-100' : 'pointer-events-none opacity-0'"
    >
      <span v-if="title" class="block text-xs font-semibold leading-snug text-white">{{ title }}</span>
      <span class="mt-1 block text-xs leading-relaxed text-gray-300">{{ text }}</span>
    </span>
  </span>
</template>
